import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';

import { cleanURI, verifyTokenIsValid, decodeToken } from '../../../services/verification';
import { changePassword } from '../../../services/recoveryPassword';

import Text from '../../ui/Text';
import Title from '../../ui/Title';
import Link from '../../ui/Link';
import Spinner from '../../ui/Spinner';
import { CardStatus, CardStatusText, IconCheck, IconContainer, IconError } from '../../ui/CardStatus';
import Input from '../../ui/Form/Input';
import { AccentButton } from '../../ui/Button';

import * as Styles from './FormStyles';

type FormValues = {
  password: string;
  confirm_password: string;
};

const validate = (values: FormValues): Partial<FormValues> => {
  const errors: Partial<FormValues> = {};

  if (!values.password) {
    errors.password = 'A senha é obrigatória';
  } else if (
    values.password.length < 8 ||
    !/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s)(?=.*[!@#$*])/.test(values.password)
  ) {
    errors.password =
      // eslint-disable-next-line max-len
      'Sua senha deve conter no mínimo: 8 caracteres, 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere especial';
  }

  if (!values.confirm_password) {
    errors.confirm_password = 'Confirme sua senha';
  } else if (values.password !== values.confirm_password) {
    errors.confirm_password = 'As senhas não conferem';
  } else if (
    values.confirm_password.length < 8 ||
    !/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s)(?=.*[!@#$*])/.test(values.confirm_password)
  ) {
    errors.confirm_password =
      // eslint-disable-next-line max-len
      'Sua senha deve conter no mínimo: 8 caracteres, 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere especial';
  }

  return errors;
};

const Form: React.FC = () => {
  const history = useHistory();

  const [rememberToken, setRememberToken] = useState('');
  const [invalidToken, setInvalidToken] = useState(false);
  const [sending, setSending] = useState(false);
  const [triedChangePassword, setTriedChangePassword] = useState(false);
  const [errorChangePassword, setErrorChangePassword] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);
  const [passwordConfirmShow, setPasswordConfirmShow] = useState(false);

  useEffect(() => {
    // catch the remember token from url
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('rememberToken');
    setRememberToken(cleanURI(urlToken));
    const tokenDecoded = decodeToken(urlToken);
    // verify token expiration
    setInvalidToken(verifyTokenIsValid(tokenDecoded));
  }, []);

  const form = useFormik<FormValues>({
    initialValues: {
      password: '',
      confirm_password: '',
    },
    validate,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        const { password } = values;
        setSending(true);
        setTriedChangePassword(false);
        await changePassword({
          password,
          rememberToken,
        });
      } catch (err) {
        setErrorChangePassword(true);
      } finally {
        setSending(false);
        setTriedChangePassword(true);
      }
    },
  });

  const togglePasswordVisiblity = (): void => {
    setPasswordShow(!passwordShow);
  };

  const togglePasswordConfirmVisiblity = (): void => {
    setPasswordConfirmShow(!passwordConfirmShow);
  };

  const showError = (inputName: keyof FormValues): boolean =>
    Boolean(form.errors[inputName] && form.touched[inputName]);

  return (
    <Styles.Container>
      {!triedChangePassword && !invalidToken && (
        <>
          <Styles.TitleContainer>
            <Title align="center" color="black" fontWeight={700} fontSize={24} marginBottom={8}>
              Crie uma nova senha!
            </Title>
            <Text align="center" color="black">
              Quase pronto, digite sua nova senha.
            </Text>
          </Styles.TitleContainer>
          <Styles.Form onSubmit={form.handleSubmit}>
            <Input
              onInputChange={form.handleChange}
              absolute={false}
              name="password"
              label="Nova senha"
              errors={showError('password') ? form.errors.password : undefined}
              placeholder="Digite sua nova senha"
              type={passwordShow ? 'text' : 'password'}
              passwordHandlers={{
                passwordShow,
                handlePasswordVisibility: () => togglePasswordVisiblity(),
              }}
              required
            />
            <Input
              onInputChange={form.handleChange}
              absolute={false}
              name="confirm_password"
              label="Confirme sua nova senha"
              errors={showError('confirm_password') ? form.errors.confirm_password : undefined}
              placeholder="Confirme sua nova senha"
              type={passwordConfirmShow ? 'text' : 'password'}
              passwordHandlers={{
                passwordShow: passwordConfirmShow,
                handlePasswordVisibility: () => togglePasswordConfirmVisiblity(),
              }}
              required
            />
            <AccentButton type="submit" disabled={sending} bgColor="primary" height={55}>
              {sending ? <Spinner size="18px" /> : 'Salvar'}
            </AccentButton>
          </Styles.Form>
          <Text align="center" color="black">
            Lembrei minha senha! <Link to="/login">Voltar ao login</Link>
          </Text>
        </>
      )}

      {triedChangePassword && !invalidToken && (
        <>
          <Styles.TitleContainer>
            {!errorChangePassword ? (
              <>
                <Title align="center" color="black" fontWeight={700} fontSize={24}>
                  Senha alterada!
                </Title>
                <CardStatus>
                  <IconContainer type="success">
                    <IconCheck />
                  </IconContainer>

                  <CardStatusText>
                    <Text color="black">
                      <strong>Parabéns!</strong>
                    </Text>
                    <Text color="black">Sua nova senha foi alterada com sucesso.</Text>
                  </CardStatusText>
                </CardStatus>
              </>
            ) : (
              <>
                <Title align="center" color="black" fontWeight={700} fontSize={24}>
                  Oops!
                </Title>
                <CardStatus>
                  <IconContainer type="danger">
                    <IconError />
                  </IconContainer>

                  <CardStatusText>
                    <Text color="black">
                      Não foi possível redefinir a sua senha. Por favor, atualize a página e tente novamente.
                    </Text>
                    <Text color="black">Caso o problema persista, entre em contato com o nosso suporte.</Text>
                  </CardStatusText>
                </CardStatus>
              </>
            )}
          </Styles.TitleContainer>

          <AccentButton type="button" bgColor="primary" height={55} onClick={() => history.push('/login')}>
            Voltar ao login
          </AccentButton>
        </>
      )}

      {invalidToken && (
        <>
          <Styles.TitleContainer>
            <Title align="center" color="black" fontWeight={700} fontSize={24} marginBottom={8}>
              Link inválido!
            </Title>
            <Text align="center" color="black">
              <strong>Ops, esse link expirou :(</strong>
            </Text>
          </Styles.TitleContainer>

          <Styles.TitleContainer>
            <Text align="left" color="black">
              Por motivos de segurança, o link de recuperação de senha expira em 24 horas.
            </Text>
            <Text align="left" color="black">
              Para gerar um novo, clique no botão abaixo:
            </Text>
          </Styles.TitleContainer>

          <AccentButton type="button" bgColor="primary" height={55} onClick={() => history.push('/forgot-password')}>
            Gerar novo link
          </AccentButton>
        </>
      )}
    </Styles.Container>
  );
};

export default Form;
